import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { BrandWordmark } from './brand/BrandWordmark';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles,
  Loader2,
  BookOpen,
  Heart,
  Users,
  FileText,
  Info,
  Shield,
  BookmarkPlus,
  HelpCircle,
  ChevronDown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Confidence level styling - heritage-focused, premium
const confidenceConfig = {
  high: {
    label: 'Strong indication',
    description: 'Multiple signals suggest this connection',
    barWidth: '100%',
    barColor: 'bg-primary',
    badge: 'bg-primary/10 text-primary border-primary/30'
  },
  moderate: {
    label: 'Likely connection',
    description: 'Signals may indicate this connection',
    barWidth: '66%',
    barColor: 'bg-primary/80',
    badge: 'bg-primary/5 text-primary border-primary/20'
  },
  low: {
    label: 'Possible connection',
    description: 'Further research may clarify',
    barWidth: '33%',
    barColor: 'bg-muted-foreground/50',
    badge: 'bg-muted/60 text-muted-foreground border-border'
  }
};

// DNA source labels for profile selector
const sourceLabels = {
  ancestry: 'AncestryDNA',
  '23andme': '23andMe',
  myheritage: 'MyHeritage',
  ftdna: 'Family Tree DNA',
  gedmatch: 'GEDmatch',
  user_uploaded: 'Custom Upload',
  unknown: 'Unknown Source'
};

// Signal type icons and labels
const signalConfig = {
  dna: { icon: <Sparkles className="w-3.5 h-3.5" />, label: 'DNA data' },
  tree: { icon: <Users className="w-3.5 h-3.5" />, label: 'Family tree' },
  records: { icon: <FileText className="w-3.5 h-3.5" />, label: 'Records' }
};

// Insight type grouping
const INSIGHT_GROUP_LABELS = {
  maternal: { title: 'Maternal Line Insights', icon: <Heart className="w-5 h-5" /> },
  paternal: { title: 'Paternal Line Insights', icon: <Users className="w-5 h-5" /> },
  general: { title: 'General Ancestral Insights', icon: <BookOpen className="w-5 h-5" /> }
};

// Insight card component
const InsightCard = ({ insight, insightIndex, profileId, onSave }) => {
  const config = confidenceConfig[insight.confidence_level] || confidenceConfig.low;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="p-4 rounded-xl border border-border/60 bg-card/60 backdrop-blur-sm hover:border-primary/30 transition-colors"
    >
      <p className="text-sm text-foreground leading-relaxed font-serif mb-4">
        {insight.narrative}
      </p>

      <div className="flex flex-wrap gap-2 mb-3">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium uppercase tracking-wider border ${config.badge}`}>
          {config.label}
        </span>
      </div>

      {insight.suggested_focus && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-secondary/40 border border-border/30 mb-3">
          <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium mb-0.5">Suggested focus</p>
            <p className="text-xs text-foreground">{insight.suggested_focus}</p>
          </div>
        </div>
      )}

      {insight.signals_used && insight.signals_used.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {insight.signals_used.map((sig, idx) => {
            const sc = signalConfig[sig.signal_type] || { icon: null, label: sig.signal_type };
            return (
              <span
                key={idx}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] text-muted-foreground bg-muted/50 border border-border/50"
              >
                {sc.icon}
                {sc.label}
              </span>
            );
          })}
        </div>
      )}

      {insight.why_suggested && (
        <Collapsible className="mb-3 group">
          <CollapsibleTrigger className="flex items-center gap-2 w-full text-left text-[11px] text-muted-foreground hover:text-foreground transition-colors">
            <HelpCircle className="w-3.5 h-3.5 shrink-0" />
            <span>Why this insight?</span>
            <ChevronDown className="w-3 h-3 shrink-0 ml-auto group-data-[state=open]:rotate-180 transition-transform" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <p className="mt-2 pl-5 text-[11px] text-muted-foreground leading-relaxed italic">
              {insight.why_suggested}
            </p>
          </CollapsibleContent>
        </Collapsible>
      )}

      {onSave && (
        <Button
          variant="outline"
          size="sm"
          className="mt-2 text-[11px] h-8 gap-1.5"
          onClick={() => onSave(insight, insightIndex)}
          data-testid={`save-insight-${insightIndex}`}
        >
          <BookmarkPlus className="w-3.5 h-3.5" />
          Save to Research Journal
        </Button>
      )}
    </motion.div>
  );
};

// Main DNA Insights Panel
const DNAInsightsPanel = ({ profiles = [] }) => {
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [trees, setTrees] = useState([]);
  const [saveDialog, setSaveDialog] = useState(null); // { insight, insightIndex }
  const [saveTreeId, setSaveTreeId] = useState('');
  const [saveNotes, setSaveNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (profiles.length > 0 && !selectedProfileId) {
      setSelectedProfileId(profiles[0].profile_id);
    }
  }, [profiles, selectedProfileId]);

  useEffect(() => {
    if (!selectedProfileId) {
      setInsights(null);
      return;
    }

    const fetchInsights = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API}/dna/insights/${selectedProfileId}`);
        setInsights(response.data);
      } catch (err) {
        console.error('Error fetching DNA insights:', err);
        setError(err.response?.data?.detail || 'Unable to load heritage insights');
        setInsights(null);
      } finally {
        setLoading(false);
      }
    };

    fetchInsights();
  }, [selectedProfileId]);

  useEffect(() => {
    const fetchTrees = async () => {
      try {
        const response = await axios.get(`${API}/trees`);
        setTrees(response.data || []);
        if (response.data?.length > 0 && !saveTreeId) {
          setSaveTreeId(response.data[0].id);
        }
      } catch (err) {
        console.error('Error fetching trees:', err);
      }
    };
    fetchTrees();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount: load trees once for save dialog
  }, []);

  const handleSaveClick = (insight, insightIndex) => {
    setSaveDialog({ insight, insightIndex });
    setSaveTreeId(trees.length > 0 ? trees[0].id : '');
    setSaveNotes('');
  };

  const handleSaveConfirm = async () => {
    if (!saveDialog || !saveTreeId) return;
    setIsSaving(true);
    try {
      const response = await axios.post(`${API}/dna/insights/save-investigation`, {
        tree_id: saveTreeId,
        profile_id: selectedProfileId,
        insight_index: saveDialog.insightIndex,
        narrative: saveDialog.insight.narrative,
        insight_type: saveDialog.insight.insight_type,
        suggested_focus: saveDialog.insight.suggested_focus || null,
        notes: saveNotes || null
      });
      if (response.data.status === 'already_saved') {
        toast.info('This insight is already in your Research Journal');
      } else {
        toast.success('DNA insight saved to Research Journal', { duration: 3000 });
      }
      setSaveDialog(null);
    } catch (err) {
      console.error('Error saving insight:', err);
      toast.error(err.response?.data?.detail || 'Unable to save to Research Journal');
    } finally {
      setIsSaving(false);
    }
  };

  if (profiles.length === 0) return null;

  const groupedInsights = insights?.insights
    ? {
        maternal: insights.insights
          .map((i, idx) => ({ ...i, _globalIndex: idx }))
          .filter((i) => i.insight_type === 'maternal'),
        paternal: insights.insights
          .map((i, idx) => ({ ...i, _globalIndex: idx }))
          .filter((i) => i.insight_type === 'paternal'),
        general: insights.insights
          .map((i, idx) => ({ ...i, _globalIndex: idx }))
          .filter((i) => i.insight_type === 'general')
      }
    : null;

  const hasAnyInsights = groupedInsights && (
    groupedInsights.maternal.length > 0 ||
    groupedInsights.paternal.length > 0 ||
    groupedInsights.general.length > 0
  );

  return (
    <Card className="bg-card border-border shadow-sm overflow-hidden" data-testid="dna-insights-panel">
      <CardHeader className="pb-4 border-b border-border/40">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="text-lg font-serif font-medium text-foreground flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Heritage Insights
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground mt-1">
              Ancestry narratives based on your DNA, family tree, and records. For genealogical exploration only.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 min-w-[200px]">
            <Label className="text-xs text-muted-foreground">Profile</Label>
            <Select value={selectedProfileId} onValueChange={setSelectedProfileId}>
              <SelectTrigger className="bg-background border-input text-foreground">
                <SelectValue placeholder="Select profile..." />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {profiles.map((p) => (
                  <SelectItem key={p.profile_id} value={p.profile_id} className="text-popover-foreground">
                    {sourceLabels[p.source] || 'DNA Profile'} • {new Date(p.uploaded_at).toLocaleDateString()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-6">
        {/* Where DNA insights come from - subtle explanatory note */}
        <div className="flex items-start gap-3 p-3 rounded-lg bg-secondary/30 border border-border/40 mb-6">
          <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium mb-0.5">
              Where DNA insights come from
            </p>
            <p className="text-xs text-foreground leading-relaxed">
              <BrandWordmark variant="inline" as="span" /> does not perform DNA testing directly. DNA data can be
              uploaded from trusted testing services, and our platform helps interpret that information alongside family
              trees, records, and family history to provide genealogical insights.
            </p>
            <p className="text-[11px] text-muted-foreground mt-1.5 italic">
              These insights are intended for family history exploration and are not medical interpretations.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-primary animate-spin mb-3" />
            <p className="text-sm text-muted-foreground">Generating heritage insights...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Info className="w-10 h-10 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        ) : !hasAnyInsights ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <BookOpen className="w-12 h-12 text-muted-foreground/60 mb-4" />
            <span className="text-sm font-medium text-foreground mb-1">No insights available yet</span>
            <p className="text-xs text-muted-foreground max-w-sm">
              Add ethnicity estimates, link to a family tree, or upload more DNA data. 
              Heritage insights may emerge as your profile becomes more complete.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Signals summary - subtle heritage tone */}
            {insights?.signals_summary && Object.values(insights.signals_summary).some((v) => v > 0) && (
              <div className="flex flex-wrap gap-3 pb-4 border-b border-border/40">
                {Object.entries(insights.signals_summary).map(([key, count]) => {
                  if (count === 0) return null;
                  const sc = signalConfig[key];
                  if (!sc) return null;
                  return (
                    <span
                      key={key}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] text-muted-foreground bg-muted/50 border border-border/50"
                    >
                      {sc.icon}
                      {sc.label}: {count}
                    </span>
                  );
                })}
              </div>
            )}

            {['maternal', 'paternal', 'general'].map((group) => {
              const items = groupedInsights[group];
              if (!items || items.length === 0) return null;

              const groupConfig = INSIGHT_GROUP_LABELS[group];
              return (
                <div key={group} className="space-y-4">
                  <div className="flex items-center gap-2 text-foreground">
                    {groupConfig.icon}
                    <h3 className="text-sm font-serif font-medium uppercase tracking-wider">
                      {groupConfig.title}
                    </h3>
                  </div>
                  <div className="space-y-3 pl-6 border-l-2 border-primary/20">
                    {items.map((insight, idx) => (
                      <InsightCard
                        key={insight._globalIndex ?? idx}
                        insight={insight}
                        insightIndex={insight._globalIndex ?? idx}
                        profileId={selectedProfileId}
                        onSave={handleSaveClick}
                      />
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Disclaimer */}
            <div className="flex items-start gap-3 p-4 rounded-xl bg-secondary/40 border border-border/50">
              <Shield className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium mb-1">
                  For genealogical exploration only
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {insights?.disclaimer ||
                    'These insights help you explore your heritage. They are not medical or health-related. Ethnicity estimates and lineage connections are probabilistic—they may change as research continues. We do not use your DNA for health screening.'}
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>

      {/* Save to Research Journal dialog */}
      <Dialog open={!!saveDialog} onOpenChange={(open) => !open && setSaveDialog(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Save to Research Journal</DialogTitle>
            <DialogDescription>
              Save this heritage insight to your Research Journal for later investigation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-muted-foreground">Tree</Label>
              <Select value={saveTreeId} onValueChange={setSaveTreeId}>
                <SelectTrigger className="mt-1 bg-background border-input">
                  <SelectValue placeholder="Select tree..." />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {trees.map((t) => (
                    <SelectItem key={t.id} value={t.id} className="text-popover-foreground">
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Notes (optional)</Label>
              <Textarea
                value={saveNotes}
                onChange={(e) => setSaveNotes(e.target.value)}
                placeholder="Add research notes..."
                className="mt-1 min-h-[60px] bg-background border-input"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialog(null)} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveConfirm}
              disabled={!saveTreeId || isSaving}
              data-testid="confirm-save-insight-btn"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookmarkPlus className="w-4 h-4" />}
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default DNAInsightsPanel;
